import { useState, useCallback } from "react";
import type {
  TemplateInformation,
  EntityItem,
  TemplateAssignmentRequest,
  FieldTemplate,
} from "../types/api-types";
import {
  getTemplateAssignment,
  saveTemplateAssignment,
} from "../services/template-service";
import { useNotification } from "../../../context/notification-provider";
import { useDialogs } from "../../dialog/use-dialog";

interface UseTemplateAssignmentReturn {
  selectedEntity: EntityItem | null;
  fieldTemplates: FieldTemplate[];
  isLoading: boolean;
  isSaving: boolean;
  handleEntityChange: (
    event: React.SyntheticEvent,
    newValue: EntityItem | null,
  ) => void;
  handleTemplateChange: (
    columnId: number,
    newTemplate: TemplateInformation | null,
  ) => void;
  handleSave: () => Promise<void>;
}

const useTemplateAssignment = (): UseTemplateAssignmentReturn => {
  const [selectedEntity, setSelectedEntity] = useState<EntityItem | null>(null);
  const [fieldTemplates, setFieldTemplates] = useState<FieldTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { showNotification } = useNotification();
  const { confirm } = useDialogs();

  const handleEntityChange = useCallback(
    (_event: React.SyntheticEvent, newValue: EntityItem | null) => {
      setSelectedEntity(newValue);

      if (newValue) {
        setIsLoading(true);
        getTemplateAssignment(newValue.id)
          .then((data) => {
            // Only show fields that have a template assigned
            const fieldsWithTemplates = data.fields.filter(
              (field) =>
                field.template !== null &&
                field.template.description !== "" &&
                field.template.id !== 0,
            );
            setFieldTemplates(fieldsWithTemplates);
          })
          .catch((err) => {
            showNotification({
              message: `Failed to load template assignments. ${(err as Error).message}`,
              severity: "error",
            });
            setFieldTemplates([]);
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        setFieldTemplates([]);
      }
    },
    [showNotification],
  );

  const handleTemplateChange = useCallback(
    (columnId: number, newTemplate: TemplateInformation | null) => {
      setFieldTemplates((prev) =>
        prev.map((item) =>
          item.field.columnId === columnId
            ? { ...item, template: newTemplate }
            : item,
        ),
      );
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!selectedEntity) {
      showNotification({
        message: "Please select an entity first",
        severity: "warning",
      });
      return;
    }

    try {
      const confirmed = await confirm(
        `Are you sure you want to save the template assignments for "${selectedEntity.description}"?`,
        {
          title: "Confirm Save",
          okText: "Save",
          cancelText: "Cancel",
          severity: "success",
        },
      );

      if (!confirmed) {
        return;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Dialog error:", err);
      showNotification({
        message: "Failed to show confirmation dialog.",
        severity: "error",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Send all fields to the API
      const assignments: TemplateAssignmentRequest[] = fieldTemplates.map(
        (ft) => ({
          tableId: selectedEntity.id,
          columnId: ft.field.columnId,
          templateId: ft.template?.id || 0,
        }),
      );

      await saveTemplateAssignment(assignments);

      showNotification({
        message: "Template assignments saved successfully",
        severity: "success",
      });
    } catch (err) {
      showNotification({
        message: `Failed to save template assignments. ${(err as Error).message}`,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedEntity, fieldTemplates, showNotification, confirm]);

  return {
    selectedEntity,
    fieldTemplates,
    isLoading,
    isSaving,
    handleEntityChange,
    handleTemplateChange,
    handleSave,
  };
};

export default useTemplateAssignment;
